import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogService,
  NbIconModule,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { finalize } from 'rxjs';
import { UsuarioTurmaVinculoDto } from '../../core/api/usuario-turma-vinculo.dto';
import { DialogAlertComponent } from '../inscricao/inscricao.dialog.component';
import { Turma } from '../../core/api/turma';
import { UsuarioResposta } from '../../core/api/usuario';
import { TurmaService } from '../../core/api/turma.service';
import { UsuarioAppService } from '../../core/api/usuario-app.service';

import { mensagemDeErro } from '../../utils/mensagem-erro';
import { ListaVaziaComponent } from '../../shared/lista-vazia/lista-vazia.component';
import { AutocompleteComponent, OpcaoAutocomplete } from '../../shared/autocomplete/autocomplete.component';
import { BarraCarregandoComponent } from '../../shared/barra-carregando/barra-carregando.component';
import { Ordenacao, OrdenavelComponent } from '../../shared/ordenavel/ordenavel.component';
import { ordenarEmMemoria } from '../../utils/ordenar-em-memoria';
/**
 * Vínculo usuário ↔ turma (acesso do catequista à turma).
 *
 * Pontos de chamada (contrato igreja-api):
 * - `GET    /turmas/{id}/usuarios`
 * - `POST   /turmas/{id}/usuarios`            body `{ usuarioId }`
 * - `DELETE /turmas/{id}/usuarios/{usuarioId}`
 * - `GET    /usuarios?page&size&filtro`       (busca de candidatos)
 */
@Component({
  selector: 'app-turma-usuario',
  standalone: true,
  imports: [
    CommonModule,
    NbCardModule,
    NbButtonModule,
    NbIconModule,
    NbSpinnerModule,
    ListaVaziaComponent,
    AutocompleteComponent,
    BarraCarregandoComponent,
    OrdenavelComponent,
  ],
  templateUrl: './turma-usuario.component.html',
  styleUrl: './turma-usuario.component.scss',
})
export class TurmaUsuarioComponent implements OnInit {
  /**
   * `TAB-01` — ordenação **em memória**, e aqui ela é legítima: o endpoint devolve a
   * coleção inteira, não uma página. Onde há paginação, ordenar em memória mentiria.
   */
  ordenacao: Ordenacao | null = null;

  ordenarPor(o: Ordenacao): void {
    this.ordenacao = o;
  }

  get vinculadosExibidos(): UsuarioTurmaVinculoDto[] {
    return ordenarEmMemoria(this.vinculados, this.ordenacao, (item, campo) => (item as unknown as Record<string, unknown>)[campo]);
  }

  turmas: Turma[] = [];
  turmaSelecionada = '';

  /** Autocomplete de turma (mesmo padrão de "Minhas turmas") */
  carregandoTurmas = false;

  vinculados: UsuarioTurmaVinculoDto[] = [];
  /** Resultado da última busca no servidor — as opções oferecidas pelo campo. */
  sugestoes: UsuarioResposta[] = [];
  usuarioSelecionado?: UsuarioResposta;

  carregandoVinculos = false;
  buscandoUsuarios = false;
  salvando = false;
  erroVinculos: string | null = null;


  constructor(
    private turmaService: TurmaService,
    private usuarioService: UsuarioAppService,
    private toastr: NbToastrService,
    private dialogService: NbDialogService,
  ) {}

  ngOnInit(): void {
    this.carregarTurmas();
  }

  /**
   * `FRM-04` — o termo vai para o servidor. Antes carregava as 500 primeiras turmas e
   * filtrava em memória: da 501 em diante, o campo dizia "nenhuma encontrada" para uma turma
   * que existe.
   */
  carregarTurmas(termo = ''): void {
    this.carregandoTurmas = true;
    this.turmaService
      .listar(0, 20, termo)
      .pipe(finalize(() => (this.carregandoTurmas = false)))
      .subscribe({
        next: (res) => {
          const achadas = res.content ?? [];
          // A escolhida continua na lista mesmo fora do resultado, senão o campo perde o rótulo.
          const escolhida = this.turmas.find((t) => t.id === this.turmaSelecionada);
          this.turmas =
            escolhida && !achadas.some((t) => t.id === escolhida.id)
              ? [escolhida, ...achadas]
              : achadas;
        },
        error: (err) => this.toastr.danger(mensagemDeErro(err, 'Não foi possível carregar as turmas.'), 'Erro'),
      });
  }

  /** Evita repetir o ano quando ele já faz parte do nome da turma (ex.: "2026 - CRISMA 1 …"). */
  labelTurma(t: Turma): string {
    const nome = (t.nome ?? '').trim();
    const ano = t.ano != null ? String(t.ano) : '';
    if (!ano || nome.includes(ano)) return nome;
    return `${nome} (${ano})`;
  }

  get opcoesTurma(): OpcaoAutocomplete[] {
    return this.turmas.map((t) => ({ id: t.id ?? '', rotulo: this.labelTurma(t) }));
  }

  onTurmaChange(turmaId: string): void {
    this.turmaSelecionada = turmaId;
    this.usuarioSelecionado = undefined;
    this.sugestoes = [];
    this.carregarVinculos();
  }

  carregarVinculos(): void {
    if (!this.turmaSelecionada) {
      this.vinculados = [];
      return;
    }

    this.carregandoVinculos = true;
    this.erroVinculos = null;
    this.turmaService.listarUsuariosTurma(this.turmaSelecionada).subscribe({
      next: (usuarios) => {
        this.vinculados = Array.isArray(usuarios) ? usuarios : [];
        this.carregandoVinculos = false;
      },
      error: (err) => {
        this.vinculados = [];
        this.carregandoVinculos = false;
        this.erroVinculos = mensagemDeErro(err, 'Não foi possível carregar os usuários da turma.');
      },
    });
  }

  /** O rótulo carrega login e perfil porque é por eles que se distingue nome repetido. */
  get opcoesUsuario(): OpcaoAutocomplete[] {
    return this.sugestoes.map((u) => {
      const perfil = this.perfilLabel(u);
      return {
        id: u.id ?? '',
        rotulo: perfil ? `${u.nome} — ${u.login} (${perfil})` : `${u.nome} — ${u.login}`,
      };
    });
  }

  get usuarioSelecionadoId(): string {
    return this.usuarioSelecionado?.id ?? '';
  }

  onUsuarioChange(usuarioId: string): void {
    this.usuarioSelecionado = this.sugestoes.find((u) => u.id === usuarioId);
  }

  buscarUsuarios(texto: string): void {
    if (texto.trim().length < 2) {
      this.sugestoes = [];
      return;
    }

    this.buscandoUsuarios = true;
    this.usuarioService.listar(0, 20, texto).subscribe({
      next: (res) => {
        const jaVinculados = new Set(this.vinculados.map((v) => v.id));
        this.sugestoes = (res.content ?? []).filter((u) => !u.id || !jaVinculados.has(u.id));
        this.buscandoUsuarios = false;
      },
      error: () => {
        this.sugestoes = [];
        this.buscandoUsuarios = false;
      },
    });
  }

  perfilLabel(usuario: UsuarioResposta): string {
    if (usuario.catequista) return 'catequista';
    if (usuario.coordenacao) return 'coordenação';
    if (usuario.admin) return 'admin';
    return '';
  }

  vincular(): void {
    if (!this.turmaSelecionada || !this.usuarioSelecionado?.id || this.salvando) return;

    const usuario = this.usuarioSelecionado;
    this.salvando = true;
    this.turmaService.vincularUsuarioTurma(this.turmaSelecionada, usuario.id!).subscribe({
      next: () => {
        this.salvando = false;
        this.usuarioSelecionado = undefined;
        this.sugestoes = [];
        this.carregarVinculos();
        this.toastr.success(`${usuario.nome} agora tem acesso à turma.`, 'Sucesso');
      },
      error: (err) => {
        this.salvando = false;
        this.toastr.danger(mensagemDeErro(err, 'Não foi possível vincular o usuário.'), 'Erro');
      },
    });
  }

  desvincular(vinculo: UsuarioTurmaVinculoDto): void {
    if (!this.turmaSelecionada) return;

    this.dialogService
      .open(DialogAlertComponent, {
        context: {
          title: 'Remover acesso',
          message: `Remover o acesso de ${vinculo.nome} a esta turma? O usuário continua cadastrado no sistema.`,
          mostrarConfirmacao: true,
          msgConfirmacao: 'Sim, remover o acesso',
        },
      })
      .onClose.subscribe((ok: boolean) => {
        if (!ok) return;
        this.turmaService.desvincularUsuarioTurma(this.turmaSelecionada, vinculo.id).subscribe({
          next: () => {
            this.carregarVinculos();
            this.toastr.success(`${vinculo.nome} não tem mais acesso a esta turma.`, 'Acesso');
          },
          error: (err) => {
            this.toastr.danger(mensagemDeErro(err, 'Não foi possível remover o acesso.'), 'Erro');
          },
        });
      });
  }

}
